/**
 * Responsibility: say how each kind of structure is measured, drawn, and masked.
 *
 * The join between two other modules. A biome decides *where and what* — towers here, trees
 * there. `structureGeometry.js` knows *how to draw* a box or a conifer. This table connects them,
 * so `tileContent.js` can size buffers and fill them without knowing that towers and trees differ.
 *
 * Adding a kind means one entry here. Nothing in the assembly pipeline changes: the two-pass
 * sizing, the shared buffers, and the depth mask are all generic over this table.
 *
 * Contains no Three.js and no DOM access.
 */

import {
  emitRing,
  emitAntenna,
  emitGableBuilding,
  emitGroundLine,
  emitRingStack,
  emitRockColumn,
  emitRow,
  emitShapeSegments,
  emitSilo,
  emitSolidMask,
  emitTintedSegments,
  maskForRingStack,
  segmentsForRingStack,
  emitTree,
  emitTreeMask,
  emitWindpump,
  emitWindTurbine,
  maskForRockColumn,
  rockColumnSolid,
  segmentsForRockColumn,
  segmentsForRow,
  segmentsForSilo,
  segmentsForTree,
  segmentsForWindpump,
  segmentsForWindTurbine,
  SEGMENTS_PER_ANTENNA,
  SEGMENTS_PER_GABLE_BUILDING,
  SEGMENTS_PER_RING,
  MASK_VERTICES_PER_TREE,
  MASK_TRIANGLES_PER_TREE,
} from './structureGeometry.js';
import { countSolidsMask } from './ringStack.js';
import { archSolid, maskForArch, segmentsForArch } from './archGeometry.js';
import { dishSolid, emitDish, maskForDish, segmentsForDish } from './dishGeometry.js';
import { emitWaterSurface } from './waterSurface.js';
import { bridgeShapeOrDefault } from './bridgeGeometry.js';
import { getBuildingShapeOrDefault, countMask } from './buildingShapes.js';
import { hexToRgb } from './palette.js';
import {
  AIRFIELD_PALETTE,
  BRIDGE_PALETTE,
  INDUSTRIAL_PALETTE,
  WATER_PALETTE,
  WIND_TURBINE_PALETTE,
} from '../config.js';

/**
 * Where a bridge's realised line list is cached, keyed by the bridge object itself.
 *
 * The same reason buildings need one: `tileContent` asks a structure for its segment count in one pass and for its
 * geometry in another, so building the shape twice per bridge would double the work for nothing. A `WeakMap` keeps this an
 * implementation detail and lets the per-tile bridge objects be collected as soon as the tile is done with them.
 */
const bridgeLines = new WeakMap();

/**
 * The bridge gradient, unpacked once.
 *
 * Every other structure is handed a gradient by `tileContent`, already converted from hex. A bridge supplies its own —
 * see the note on the kind below — so it has to do that conversion itself, and doing it per segment would be absurd.
 */
/**
 * Fixed gradient for wind turbines, for the reason a bridge has one.
 *
 * Drawn in farmland's own colours a turbine would sink into the fields it stands in, and a row of them is the
 * tallest thing for a long way in any direction — they are meant to be seen from the far side of a valley.
 */
const TURBINE_GRADIENT = {
  low: hexToRgb(WIND_TURBINE_PALETTE.LOW),
  high: hexToRgb(WIND_TURBINE_PALETTE.HIGH),
};

const BRIDGE_GRADIENT = {
  low: hexToRgb(BRIDGE_PALETTE.LOW),
  high: hexToRgb(BRIDGE_PALETTE.HIGH),
};

/** The water gradient, unpacked once. Fixed for the same reason the bridge one is — see the kinds below. */
const WATER_GRADIENT = {
  low: hexToRgb(WATER_PALETTE.LOW),
  high: hexToRgb(WATER_PALETTE.HIGH),
};

/** The airfield gradient, unpacked once. Fixed, like the bridge and water ones — see the kind below. */
const AIRFIELD_GRADIENT = {
  low: hexToRgb(AIRFIELD_PALETTE.LOW),
  high: hexToRgb(AIRFIELD_PALETTE.HIGH),
};

/**
 * The industrial gradient, unpacked once. Fixed for the same reason the airfield's is.
 *
 * Hot amber where the airfield is cold white, and the two share a host biome — so on the same open ground a works
 * and an airfield are separable at a glance, which they would not be drawn in baseline's own muted palette.
 */
const INDUSTRIAL_GRADIENT = {
  low: hexToRgb(INDUSTRIAL_PALETTE.LOW),
  high: hexToRgb(INDUSTRIAL_PALETTE.HIGH),
};

/** A bridge's line segments, built once. */
function bridgeLinesFor(bridge) {
  const cached = bridgeLines.get(bridge);
  if (cached) return cached;

  const lines = bridgeShapeOrDefault(bridge.archetype).build(bridge);
  bridgeLines.set(bridge, lines);
  return lines;
}

/**
 * The spec a building's archetype is built from.
 *
 * Derived here rather than stored on the building, so the placement code in `city.js` stays about
 * *where* things go and this module stays the only place that knows how a shape is driven.
 *
 * The base sits at the building's highest ground corner and `sampleBaseY` returns each corner's own
 * height, which is what lets a footprint drape over a slope while the roof stays level.
 */
function specFor(building) {
  /*
    Ground for the shape's own base points, sampled by the placement code because that is what already
    owns terrain sampling. Falls back to the rectangular corners for a building described before shapes
    existed, so nothing has to be migrated.
  */
  const basePoints = building.baseGroundY ?? null;

  return {
    centerX: building.centerX,
    centerZ: building.centerZ,
    width: building.width,
    depth: building.depth,
    baseY: building.roofY - building.height,
    height: building.height,

    /**
     * Ground height at one of the shape's base points.
     *
     * Nearest of the pre-sampled points. In practice it is an exact hit — the same spec regenerates the
     * same points in the same order — so this is a lookup, and the distance search is only there so a
     * near miss degrades to the closest real sample instead of guessing.
     */
    sampleBaseY(x, z) {
      const points = basePoints ?? fallbackCorners(building);

      let bestY = points[0][2];
      let bestDistance = Infinity;

      for (const [px, pz, py] of points) {
        const distance = Math.hypot(x - px, z - pz);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestY = py;
        }
      }

      return bestY;
    },
  };
}

/** Footprint corner order, matching `city.js` and `emitBox`. */
const CORNER_OFFSETS = [
  [-1, -1],
  [1, -1],
  [1, 1],
  [-1, 1],
];

/** The four rectangular corners as base points, for buildings that carry no shape-specific samples. */
function fallbackCorners(building) {
  const halfWidth = building.width / 2;
  const halfDepth = building.depth / 2;

  return CORNER_OFFSETS.map(([sx, sz], i) => [
    building.centerX + sx * halfWidth,
    building.centerZ + sz * halfDepth,
    building.cornerGroundY[i],
  ]);
}

/** The archetype a building uses, resolved once so counting and emitting cannot disagree. */
function shapeFor(building) {
  return getBuildingShapeOrDefault(building.shape);
}

/**
 * Where a building's realised geometry is cached, keyed by the building object itself.
 *
 * ## Why this is needed
 *
 * `tileContent` sizes its buffers in one pass and fills them in another, and the mask is counted and
 * emitted separately again — so a building is asked about its geometry four times. Building the shape on
 * each ask made a city tile go from 2.5 ms to 9.9 ms, with a worst case of 24 ms against a 16.7 ms frame.
 *
 * A `WeakMap` rather than a field on the building, so this stays an implementation detail of this module
 * and never appears in the plain data a biome emits. Buildings are transient per-tile objects, so entries
 * are collected as soon as a tile is done with them.
 */
const geometryCache = new WeakMap();

/**
 * A building's spec, lines, solids and mask counts, computed once.
 *
 * @param {object} building
 * @returns {{spec: object, lines: Array<object>, solids: Array<object>, mask: {vertices: number, triangles: number}}}
 */
function geometryFor(building) {
  const cached = geometryCache.get(building);
  if (cached) return cached;

  const shape = shapeFor(building);
  const spec = specFor(building);

  const geometry = {
    shape,
    spec,
    lines: shape.build(spec),
    solids: shape.masked ? shape.solids(spec) : [],
    mask: countMask(shape, spec),
  };

  geometryCache.set(building, geometry);
  return geometry;
}

/** How many line segments one building will produce, including its accent detail. */
function segmentsForBuilding(building) {
  return (
    geometryFor(building).lines.length +
    building.accentRings.length * SEGMENTS_PER_RING +
    (building.antenna ? SEGMENTS_PER_ANTENNA : 0)
  );
}

/**
 * Every structure kind the world can build.
 *
 * `maskTriangles` returning 0 means the kind casts no depth mask.
 */
export const STRUCTURE_KINDS = {
  tower: {
    segments: segmentsForBuilding,

    /*
      Counted from the archetype's real ring stacks rather than from a per-shape constant. A shape that
      changed its ring count would otherwise silently overrun the buffer `tileContent` sized for it.

      The lattice reports zero: its wireframe *is* the structure, so there is no solid surface for a
      mask to represent, and masking an open frame would make it occlude like a slab.
    */
    maskVertices: (building) => geometryFor(building).mask.vertices,
    maskTriangles: (building) => geometryFor(building).mask.triangles,

    emit(writer, building, gradient, accentColor) {
      emitShapeSegments(writer, geometryFor(building).lines, gradient);

      /*
        Accent rings still use the building's rectangular footprint rather than the archetype's, so a
        lit floor on a cylinder is a square. Left alone deliberately: at flight distance the rings read
        as a band of light, and matching each archetype's footprint would mean a second shape pipeline
        for a difference nobody can see.
      */
      const footprint = {
        centerX: building.centerX,
        centerZ: building.centerZ,
        width: building.width,
        depth: building.depth,
      };

      for (const ringY of building.accentRings) {
        emitRing(writer, { ...footprint, y: ringY }, accentColor);
      }

      if (building.antenna) {
        emitAntenna(
          writer,
          building.centerX,
          building.centerZ,
          building.antenna.fromY,
          building.antenna.toY,
          accentColor,
        );
      }
    },

    emitMask(maskWriter, building) {
      // Empty for the lattice, whose wireframe is the structure and has no surface to mask.
      for (const solid of geometryFor(building).solids) {
        emitSolidMask(maskWriter, solid);
      }
    },
  },

  tree: {
    segments: (tree) => segmentsForTree(tree.secondTierY !== null),

    /*
      A pyramid over the crown. The four spokes the tree draws are this pyramid's edges, so the mask
      covers exactly the silhouette the tree covers.

      Trees initially shipped unmasked, on the reasoning that a hull around a spiky shape would
      occlude more than it should and that trees stay too sparse to pile up. Flying it showed the
      second half of that was wrong: looking *down* through a stand puts dozens of crowns on one
      line of sight and they merge into a flat mass of green. The first half was wrong too — the
      hull and the silhouette are the same shape.
    */
    maskVertices: () => MASK_VERTICES_PER_TREE,
    maskTriangles: () => MASK_TRIANGLES_PER_TREE,

    emit(writer, tree, gradient, accentColor) {
      /*
        An accent tree is drawn entirely in the warm colour rather than gradient-shaded. A handful
        scattered through a green stand is what stops a patch of forest reading as one flat mass.
      */
      const treeGradient = tree.accent ? { low: accentColor, high: accentColor } : gradient;
      emitTree(writer, tree, treeGradient);
    },

    emitMask(maskWriter, tree) {
      emitTreeMask(maskWriter, tree);
    },
  },

  /**
   * A crop row: a small raised ridge of worked ground.
   *
   * **This is what makes farmland read as farmland.** The first version of the biome drew field boundaries
   * and no rows at all, which came out as lines painted on the ground rather than as land that had been
   * worked. Rows are raised, closely spaced, and take their direction from the terrain's contour.
   */
  cropRow: {
    segments: (row) => segmentsForRow(row.segments),

    // No mask: a ridge a few units high would occlude almost nothing and cost a triangle pair per row.
    maskVertices: () => 0,
    maskTriangles: () => 0,

    emit(writer, row, gradient) {
      emitRow(writer, row, row.sampleGround, gradient, row.maxElevation);
    },

    emitMask() {},
  },

  /** A field boundary hedge. Supporting detail now that the rows carry the texture. */
  hedgerow: {
    segments: (hedge) => hedge.segments,

    maskVertices: () => 0,
    maskTriangles: () => 0,

    emit(writer, hedge, gradient) {
      emitGroundLine(writer, hedge, hedge.sampleGround, gradient, hedge.maxElevation);
    },

    emitMask() {},
  },

  /**
   * Any box with a pitched roof — a farmhouse, a barn, a suburban house.
   *
   * Every other building in the world is flat-topped, so a triangular roofline is instantly not-a-tower. That
   * is its whole job: rows of worked ground are ambiguous alone, but rows beside a barn are unmistakably a
   * farm, and a field of small pitched roofs is unmistakably a town.
   *
   * **Named for the geometry rather than the biome that first wanted it.** It was `farmBuilding` until
   * suburbia needed exactly the same box; a kind says *how to draw*, not who asked, and two registry entries
   * sharing one emitter would have been duplication with a straight face.
   */
  gableBuilding: {
    segments: () => SEGMENTS_PER_GABLE_BUILDING,

    /*
      Left unmasked. These are small — a barn is a fifth the height of a modest tower — so what they would
      hide is negligible, and the pitched roof would need its own mask geometry rather than reusing the
      generic ring-stack one.
    */
    maskVertices: () => 0,
    maskTriangles: () => 0,

    emit(writer, building, gradient) {
      emitGableBuilding(writer, building, gradient);
    },

    emitMask() {},
  },

  /**
   * A standing rock column — a spire or a hoodoo.
   *
   * **Masked, unlike the other slender structures here.** A windpump and a lattice tower are open frames with no
   * surface to represent, but a rock column is solid, and a field of them stacks along a line of sight the same way a
   * stand of trees does. The ring-stack mask emitter written for buildings serves it unchanged.
   */
  rockColumn: {
    segments: (column) => segmentsForRockColumn(column.profile, column.sides),

    maskVertices: (column) => maskForRockColumn(column.profile, column.sides).vertices,
    maskTriangles: (column) => maskForRockColumn(column.profile, column.sides).triangles,

    emit(writer, column, gradient) {
      emitRockColumn(writer, column, gradient);
    },

    emitMask(maskWriter, column) {
      emitSolidMask(maskWriter, rockColumnSolid(column));
    },
  },

  /**
   * A natural rock arch — two legs and a span with a hole through it.
   *
   * **Geometrically the same thing as a rock column.** A column threads polygon rings up a vertical line; an arch
   * threads them along a curve. So the ring-stack line emitter and the ring-stack mask emitter both serve it with
   * no arch-specific code, which is why the one shape in this world that wireframe renders better than solid
   * graphics do was also among the cheapest to add.
   *
   * Masked, like the columns and for the same reason: it is solid rock, and an unmasked span would let the far leg
   * show through the near one.
   */
  arch: {
    segments: segmentsForArch,

    maskVertices: (arch) => maskForArch(arch).vertices,
    maskTriangles: (arch) => maskForArch(arch).triangles,

    emit(writer, arch, gradient) {
      emitRingStack(writer, archSolid(arch), gradient, arch);
    },

    emitMask(maskWriter, arch) {
      emitSolidMask(maskWriter, archSolid(arch));
    },
  },

  /**
   * A bridge over water: a shallow arch for a modest span, a suspension bridge for a wide one.
   *
   * **The only structure drawn in a fixed gradient rather than its biome's**, and the reason is that its biome's gradient
   * is the *water's*. A bridge in water colours is invisible against the river it crosses. It is steel wherever it stands.
   *
   * Its segments come back in exactly the shape `emitShapeSegments` already consumes for every building in the world, so
   * bridges needed no new emitter at all.
   *
   * No depth mask. A deck is a thin ribbon, and masking it would hide the arch or the cables beneath — the very things
   * that make a bridge legible.
   */
  bridge: {
    segments: (bridge) => bridgeLinesFor(bridge).length,

    maskVertices: () => 0,
    maskTriangles: () => 0,

    emit(writer, bridge) {
      emitShapeSegments(writer, bridgeLinesFor(bridge), BRIDGE_GRADIENT);
    },

    emitMask() {},
  },

  /**
   * A solid of revolution on an industrial site: a cooling tower, a chimney, a storage tank, a pressure sphere.
   *
   * **The hyperboloid is the reason this feature was cheap.** A cooling tower is a ring stack with a radius that
   * narrows to a waist and flares again, and ring stacks have been emitted since the rock columns — so the line
   * emitter, the segment count and the depth mask all existed already. It needed a radius profile.
   *
   * Masked, unlike the frame kind below. These are concrete and sheet steel, and a field of unmasked cooling towers
   * is a see-through tangle of rings where the near one should hide the far one. The same call the rock columns and
   * the telescope dishes make.
   *
   * Drawn in a fixed gradient, which is the exception the bridges, the water and the airfields take: open ground is
   * deliberately muted so it recedes, and a works drawn in it would disappear.
   */
  industrialSolid: {
    segments: (part) => segmentsForRingStack(part.rings, part.sides),

    maskVertices: (part) => maskForRingStack(part.rings, part.sides).vertices,
    maskTriangles: (part) => maskForRingStack(part.rings, part.sides).triangles,

    emit(writer, part) {
      emitRingStack(writer, part.solid, INDUSTRIAL_GRADIENT, part);
    },

    emitMask(maskWriter, part) {
      emitSolidMask(maskWriter, part.solid);
    },
  },

  /**
   * The open steelwork of an industrial site: lattice pylons, their conductors, sheds and pipe racks.
   *
   * Separate from the solids above because the *masking* differs, which is the only thing a structure kind decides.
   * A pylon is an open frame with no surface to represent, and masking one would make it occlude like a slab — the
   * same reasoning that leaves the lattice tower and the windpump unmasked.
   *
   * One structure for a whole site's frame rather than one per pylon: they are built together, disposed together,
   * and thousands of tiny objects per tile cost more in allocation than the geometry costs to draw.
   */
  industrialFrame: {
    segments: (frame) => frame.lines.length,

    maskVertices: () => 0,
    maskTriangles: () => 0,

    emit(writer, frame) {
      emitShapeSegments(writer, frame.lines, INDUSTRIAL_GRADIENT);
    },

    emitMask() {},
  },

  /**
   * A vineyard's planting: vine rows and retaining walls, as the level curves of the hillside.
   *
   * **Coloured by a carried tint rather than by height, and that is the whole reason this is one kind.** A
   * retaining wall and the vine row beside it are a few units apart on a 360-unit hillside, so any colouring
   * derived from elevation would make them identical — and telling them apart is the difference between
   * reading terraces and reading hatching. The tint is stored per vertex, so one gradient and one structure
   * carry both.
   *
   * One structure holds a whole tile's planting, like a water surface rather than like a crop row. There are
   * hundreds of short segments to a tile and as objects they would cost more in allocation than the geometry
   * costs to draw.
   *
   * No depth mask. These are lines lying on the ground; masking them would make a hillside of vines occlude
   * like a slab, hiding the terraces behind it — which are the point.
   */
  vineyardLines: {
    segments: (planting) => planting.count,

    maskVertices: () => 0,
    maskTriangles: () => 0,

    emit(writer, planting, gradient) {
      emitTintedSegments(writer, planting.data, planting.count, gradient);
    },

    emitMask() {},
  },

  /**
   * One theme park ride: a ferris wheel, a coaster, a big top, a row of stalls.
   *
   * **One kind for every ride, because they differ only in shape and shape is data here.** A wheel and a tent have
   * nothing in common geometrically, but they need identical *handling* — a list of segments to draw and a list of
   * ring stacks to mask — and a kind decides handling, not identity. Ten registry entries calling the same two
   * emitters would have been duplication with a straight face; the same reasoning that made one `gableBuilding`
   * serve a barn and a suburban house.
   *
   * ## Why the geometry arrives already built
   *
   * `tileContent` asks for a segment count before it asks for segments, and a ride's lines cannot be counted by
   * formula: `linesForSolid` drops degenerate segments, so a hall's collapsed ridge ring emits fewer lines than its
   * point count implies. A second formula tracking that would be exactly the duplication `segmentsForTree` needed a
   * test to catch. So the placer builds each ride once and hands the list over, as the airfields and town features do.
   *
   * The mask *is* counted by formula, because it has to be: the buffer is sized before anything is written. That one
   * count lives next to the emitter it must agree with, in `ringStack.js`.
   *
   * Masked only where the ride has solids. A tent is canvas and a carousel roof is a lid, so their near faces must
   * hide their far ones; a wheel, a coaster and a lattice drop tower are open steel and supply no solids at all —
   * masking one would make it occlude like a slab, the same call the lattice tower and the pylons make.
   *
   * Drawn in the park's own gradient rather than a fixed one, like a town feature. A park has exactly one palette to
   * give and everything on the site should share it: pink is what keeps a fairground apart from the city's cyan and
   * industry's amber at fog distance.
   */
  parkRide: {
    segments: (ride) => ride.lines.length,

    maskVertices: (ride) => countSolidsMask(ride.solids).vertices,
    maskTriangles: (ride) => countSolidsMask(ride.solids).triangles,

    emit(writer, ride, gradient) {
      emitShapeSegments(writer, ride.lines, gradient);
    },

    emitMask(maskWriter, ride) {
      for (const solid of ride.solids) {
        emitSolidMask(maskWriter, solid);
      }
    },
  },

  /**
   * Anything in a town that is not a house: a church, a school and its playing field, a water tower, a street.
   *
   * **One kind for all of them, because they differ only in shape and shape is data here.** Each arrives as a
   * finished list of segments, so the registry needs no branch on which it is — the same arrangement the airfield
   * uses, and for the same reason: a street and a playing field have to be draped over the ground, and the terrain
   * sampler only exists at collect time.
   *
   * Drawn in the town's own gradient rather than a fixed one, unlike the bridges and the water. A town has exactly
   * one palette to give and everything in it should share it — the warm sodium ramp is what distinguishes a town
   * from the cold city it sits beside.
   *
   * No depth mask. Houses are unmasked for being small, and these are either open frame (a water tower), thin
   * ribbons on the ground (streets, a pitch), or rare enough that a masked one would be the only occluding
   * building in a town — which would read as an error rather than as depth.
   */
  townFeature: {
    segments: (feature) => feature.lines.length,

    maskVertices: () => 0,
    maskTriangles: () => 0,

    emit(writer, feature, gradient) {
      emitShapeSegments(writer, feature.lines, gradient);
    },

    emitMask() {},
  },

  /**
   * An airfield: up to three runways on an embankment, one control tower, and rarely an aircraft parked on the strip.
   *
   * **The one structure whose geometry arrives already built.** Every other kind is described by placement and realised
   * here, but an airfield's embankments need the ground under each runway edge, and the terrain sampler only exists at
   * collect time. Water solves the same problem the same way.
   *
   * Drawn in a fixed gradient rather than baseline's, which is the exception bridges and water take. Baseline's palette is
   * deliberately muted so open ground recedes into the distance, and that is exactly wrong for the largest man-made thing
   * in the world.
   *
   * No depth mask. A runway is a flat ribbon on the ground and a tower is an open frame, so there is no solid surface for a
   * mask to represent — and masking the strip would hide the embankment lines that explain why it is up in the air.
   */
  airfield: {
    segments: (airfield) => airfield.lines.length,

    maskVertices: () => 0,
    maskTriangles: () => 0,

    emit(writer, airfield) {
      emitShapeSegments(writer, airfield.lines, AIRFIELD_GRADIENT);
    },

    emitMask() {},
  },

  /**
   * A river's water surface: a grid of lines lying across the ground, clipped to the waterline.
   *
   * **The only structure that is not an object standing on the ground**, and the reason it is a structure at all is
   * that the alternative was invasive. Clamping the drawn terrain to a water level would mean teaching the heightfield
   * what water is, on the hottest path in the project, and teaching the palette to colour something other than
   * elevation. This pipeline already writes arbitrary coloured lines and already supports a per-biome gradient, so
   * water needed no change to the heightfield, the palette, `tile.js` or the renderer.
   *
   * One structure holds a whole tile's worth of segments, like a crop row rather than like a tree — thousands of tiny
   * objects per tile would cost more in allocation than the geometry does to draw.
   *
   * No depth mask. A water surface is a thin sheet, and masking it would make it occlude like a slab, hiding the far
   * bank when the river is seen at a shallow angle.
   */
  waterSurface: {
    segments: (water) => water.count,

    maskVertices: () => 0,
    maskTriangles: () => 0,

    /*
      Drawn in a fixed water gradient rather than its biome's, which is the same exception bridges take and for a sharper
      reason: **a biome can hold both water and something else.** Wooded lakes need their trees drawn in green and their
      water in cyan, and a biome has only one `structurePalette` to give. Water looking like water everywhere is also
      simply correct.
    */
    emit(writer, water) {
      emitWaterSurface(writer, water, WATER_GRADIENT);
    },

    emitMask() {},
  },

  /**
   * A radio telescope dish.
   *
   * **The only curved surface in the world.** Everything else here is boxes, cones and lines, so a stack of
   * concentric rings reads as a dish immediately — there is nothing else it could be, which is why an array needs no
   * supporting buildings to explain itself.
   *
   * Geometrically it is a ring stack like a rock column, only threaded along a tilted axis instead of a vertical one,
   * so the shared emitters serve it. Masked, because a dish is sheet metal and its near half should hide its far
   * half; the mount is an open frame and contributes nothing to the mask, like the lattice tower.
   */
  dish: {
    segments: segmentsForDish,

    maskVertices: (dish) => maskForDish(dish).vertices,
    maskTriangles: (dish) => maskForDish(dish).triangles,

    emit(writer, dish, gradient) {
      emitDish(writer, dish, gradient);
    },

    emitMask(maskWriter, dish) {
      emitSolidMask(maskWriter, dishSolid(dish));
    },
  },

  /** A grain silo. Vertical and slender, so it says "farm" from further away than the barn does. */
  silo: {
    segments: (silo) => segmentsForSilo(silo.sides),

    maskVertices: () => 0,
    maskTriangles: () => 0,

    emit(writer, silo, gradient) {
      emitSilo(writer, silo, gradient);
    },

    emitMask() {},
  },

  /**
   * A farm windpump. Kansas farmyard, not a wind turbine.
   *
   * **Static.** Motion is not a general tool here: the world is a pure function of position, and animating
   * one thing invites animating everything.
   */
  /**
   * A large wind turbine, in a row of them. Not the farm windpump above — nearly ten times its height.
   *
   * **Static, like everything else here.** What replaces motion is that every turbine in a row carries its own
   * rotor angle, which is what a photograph of a wind farm looks like anyway.
   */
  windTurbine: {
    segments: (turbine) =>
      segmentsForWindTurbine({
        sides: turbine.towerSides,
        rings: turbine.towerRings,
        blades: turbine.blades,
        chords: turbine.bladeChords,
      }),

    /*
      Unmasked, like the windpump and the transmission pylons. A tower 300 units tall and 30 wide is nearly all
      silhouette, so a mask would buy almost nothing and cost a ring stack per turbine — nine of them per farm.
      The blades could not be masked meaningfully at all.
    */
    maskVertices: () => 0,
    maskTriangles: () => 0,

    /*
      Its own gradient rather than the host biome's, the same deliberate exception a bridge gets. In farmland's
      colours a turbine would sink into the fields it stands in, and it is the tallest thing for a long way in
      any direction.
    */
    emit(writer, turbine) {
      emitWindTurbine(writer, turbine, TURBINE_GRADIENT);
    },

    emitMask() {},
  },

  windpump: {
    segments: (pump) => segmentsForWindpump(pump.blades),

    /*
      Unmasked, like a transmission pylon. Its wireframe *is* the structure — there is no solid surface for a
      mask to represent, and masking an open frame would make it occlude like a slab.
    */
    maskVertices: () => 0,
    maskTriangles: () => 0,

    emit(writer, pump, gradient) {
      emitWindpump(writer, pump, gradient);
    },

    emitMask() {},
  },
};

/**
 * Look up how to handle a structure.
 *
 * @param {{kind: string}} structure
 * @returns {object} The kind's geometry handlers.
 * @throws {Error} On an unregistered kind. Failing loudly beats silently emitting nothing, which
 *   would lose a whole biome's structures with no indication of why.
 */
export function kindOf(structure) {
  const kind = STRUCTURE_KINDS[structure?.kind];
  if (!kind) {
    throw new Error(
      `structureKinds: no geometry registered for structure kind "${structure?.kind}"`,
    );
  }
  return kind;
}

/** Exported so tests and callers can reason about geometry budgets. */
export { segmentsForBuilding };


